import { FC, useCallback, useMemo } from 'react';
import { FIELD_COLUMNS } from 'constants/Field.constants';
import { useDefaultTableProps } from 'hooks/useDefaultTableProps';
import { useSortColumnsModal } from 'hooks/useSortColumnsModal';
import { getText } from 'i18n/intl';
import { keyBy, mapValues } from 'lodash';
import { selectObjectTypesMap } from 'slices/objectTypes.slice';
import { useAppSelector } from 'slices/store';
import { TObjectExportJob } from 'types/objectJob.type';
import { TField } from '@timeedit/types';
import { EditableTable, Typography } from '@timeedit/ui-components';
import { Button, Modal } from '@timeedit/ui-components/antd';
import './JobObjectsModal.scss';

const { Text } = Typography;

type Props = {
  open: boolean;
  job: TObjectExportJob;
  onClose: () => void;
  objectTypeName: string;
  fieldIds: number[];
  fields: Record<number, TField>;
};

const JobFieldsModal: FC<Props> = (props) => {
  const allObjectTypes = useAppSelector(selectObjectTypesMap);
  const [ref, defaultTableProps, _height] = useDefaultTableProps(0);

  const dataSource = useMemo(() => {
    return props.fieldIds.map((fieldId) => {
      const field = props.fields[fieldId];
      return {
        id: fieldId,
        name: field?.name ?? `Field ${fieldId}`,
        extId: field?.extId ?? '',
        fieldType: field?.fieldType ?? '',
        objectTypes:
          field?.types.map(
            (typeId) => allObjectTypes[typeId]?.name ?? `ObjectType ${typeId}`
          ) ?? [],
        description: field?.description ?? '',
      };
    });
  }, [props.fieldIds, props.fields, allObjectTypes]);

  const updatedColumns = useMemo(() => {
    const unsortableObjects = mapValues(FIELD_COLUMNS, (col) => ({
      ...col,
      label: col.title,
      sorter: undefined,
      ellipsis: true,
    }));

    return keyBy(unsortableObjects, 'key');
  }, [FIELD_COLUMNS]);

  const { sortColumnsModal, openSortColumnsModal, sortedColumns } =
    useSortColumnsModal(updatedColumns);

  const title = useCallback(() => {
    return (
      <div className="job-objects-modal__table-title">
        <Button onClick={openSortColumnsModal} size="small">
          {getText('ui.table.edit_columns')}
        </Button>
      </div>
    );
  }, [openSortColumnsModal]);

  return (
    <Modal
      destroyOnHidden
      onOk={props.onClose}
      onCancel={props.onClose}
      closable={false}
      open={props.open}
      width={'75vw'}
      cancelButtonProps={{
        style: { display: 'none' },
      }}
    >
      <div className="job-objects-modal__title">
        <Text>
          <Text strong>Object type:</Text> {props.objectTypeName}{' '}
          <Text strong>Fields:</Text> {props.job.fields?.length ?? 0}
        </Text>
      </div>
      <div ref={ref} className="job-objects-modal__table-container">
        <EditableTable
          {...defaultTableProps}
          loading={false}
          dataSource={dataSource}
          title={title}
          columns={sortedColumns}
          virtual={true}
          fitColumns={false}
        />
        {sortColumnsModal}
      </div>
    </Modal>
  );
};

export default JobFieldsModal;
